import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";

export type SavedItemRow = Tables<"saved_items">;
export type SavedItemType = "event" | "post" | "product" | "profile" | "track" | "place";

export function useFollowing(userId: string | null) {
  return useQuery({
    queryKey: ["follows", "following", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase.from("follows").select("*").eq("follower_id", userId!);
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useFollowers(userId: string | null) {
  return useQuery({
    queryKey: ["follows", "followers", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase.from("follows").select("*").eq("following_id", userId!);
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useToggleFollow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ followerId, followingId, isFollowing }: { followerId: string; followingId: string; isFollowing: boolean }) => {
      if (isFollowing) {
        const { error } = await supabase
          .from("follows")
          .delete()
          .eq("follower_id", followerId)
          .eq("following_id", followingId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("follows").insert({ follower_id: followerId, following_id: followingId });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["follows"] });
      qc.invalidateQueries({ queryKey: ["posts", "following"] });
    },
  });
}

export function useSavedItems(userId: string | null, itemType?: SavedItemType) {
  return useQuery({
    queryKey: ["saved_items", userId, itemType ?? "all"],
    enabled: !!userId,
    queryFn: async () => {
      let query = supabase.from("saved_items").select("*").eq("user_id", userId!);
      if (itemType) query = query.eq("item_type", itemType);
      const { data, error } = await query.order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as SavedItemRow[];
    },
  });
}

export function useToggleSaved() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      userId: string;
      itemType: SavedItemType;
      itemId: string;
      metadata?: Record<string, unknown>;
      isSaved: boolean;
    }) => {
      if (args.isSaved) {
        const { error } = await supabase
          .from("saved_items")
          .delete()
          .eq("user_id", args.userId)
          .eq("item_type", args.itemType)
          .eq("item_id", args.itemId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("saved_items").insert({
          user_id: args.userId,
          item_type: args.itemType,
          item_id: args.itemId,
          metadata: (args.metadata ?? {}) as never,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["saved_items"] }),
  });
}

/** Public profiles, used by discovery/creators/venues pages. */
export function useProfilesByType(userType: "creator" | "venue" | "groover") {
  return useQuery({
    queryKey: ["profiles", userType],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, username, full_name, avatar_url, bio, city, country, user_type, verification_status, subscription_status, available_for_work, availability_note, gallery, pricing, social_links, website, latitude, longitude, created_at, profile_views")
        .eq("user_type", userType)
        .order("created_at", { ascending: false })
        .limit(60);
      if (error) throw error;
      return data ?? [];
    },
  });
}
